import { useTranslation } from 'react-i18next';
import React from 'react';
import { GetUsersResponseDto } from '../backend/openapi/api-types';
import { usePathBuilder } from '../../core/routing/hooks/use-path-builder';
import { createDisplayColumn, makeAccessorColumnBuilder } from '../data-table/helpers';
import { TableCellIconLabel } from '../../visual-components/table/table-cells/table-cell-icon-label/table-cell-icon-label';
import { DataTable } from '../data-table/data-table';
import { createColumns } from '../data-table/create-columns';
import { TableLayout } from '../../visual-components/table-layout/table-layout';
import { Table } from '../../visual-components/table/table/table';
import { ColumnDefinition } from '../data-table/types';

export const useUserTableColumnDefinition = ({ isCustomerAdmin = false }: { isCustomerAdmin: boolean | undefined }) => {
  const { t } = useTranslation();
  const { formDeleteUser } = usePathBuilder();
  const createAccessorColumn = makeAccessorColumnBuilder<GetUsersResponseDto>();

  const colDef = [
    createAccessorColumn('name', {
      headerLabel: t('form.id.name'),
      cssWidth: '400px',
      renderCell: ({
        value,
        row: {
          original: { isSelf },
        },
      }) => {
        if (isSelf) {
          return (
            <TableCellIconLabel iconPosition="right" variant="user-header">
              {value}
            </TableCellIconLabel>
          );
        } else {
          return value;
        }
      },
    }),
    createAccessorColumn('email', {
      headerLabel: t('global.email'),
      cssWidth: '400px',
    }),
    createAccessorColumn('isVerified', {
      headerLabel: t('profile.users.isVerified'),
      cssWidth: '190px',
      renderCell: ({ value }) => {
        if (value) {
          return <TableCellIconLabel variant="check" />;
        }
        return <TableCellIconLabel variant="cross" />;
      },
    }),
    createDisplayColumn({
      columnId: 'delete',
      headerLabel: <></>,
      cssWidth: '80px',
      isHidden: !isCustomerAdmin,
      renderCell: ({ rowId }) => (
        <TableCellIconLabel
          iconColor="red"
          link={{ ariaLabel: t('form.deleteUser.documentTitle'), to: formDeleteUser(rowId) }}
          variant="trashcan"
        />
      ),
    }),
  ];

  return colDef;
};

type Props = {
  users: GetUsersResponseDto[];
  colDef: ColumnDefinition<GetUsersResponseDto>[];
  hasError?: boolean;
  isLoading?: boolean;
};

export const UserTable: React.FC<Props> = ({ users, colDef, hasError, isLoading }) => {
  const { t } = useTranslation();

  return (
    <DataTable
      data={users}
      hasError={hasError}
      isLoading={isLoading}
      columns={createColumns({
        colDef,
        rowIdentifierColumnId: 'email',
        lightHeader: true,
      })}
    >
      {(props) => (
        <TableLayout
          table={<Table {...props} label={t('profile.usersTable.title')} />}
          title={t('profile.usersTable.title')}
        />
      )}
    </DataTable>
  );
};
